// frontend/src/pages/Insights.jsx
import React, { useMemo, useState } from "react";
import { useQuery } from "@apollo/client";
import { Typography, Card } from "antd";
import { Link } from "react-router-dom";
import Tab from "../components/Tabs";
import PeriodSelector from "../components/PeriodSelector";
import NestedHoldingsTable from "../charts/NestedHoldingsTable";
import COMMON_HOLDINGS from "../graphql/common_holdings";
import COMMON_ADDITIONS from "../graphql/common_additions";
import COMMON_DELETIONS from "../graphql/common_deletions";
import numberFormat from "../utils/numberFormat";
import { getHoldingPeriod } from "../utils/dateUtils";
import "../styles/Insights.css";

const { Title } = Typography;

/**
 * Insights Page
 * 
 * Displays:
 * - Common Additions (by % change)
 * - Common Deletions (by % change)
 * - Common Holdings (by value)
 * 
 * Removed:
 * - Common New Entries (feature deprecated - no reliable way to identify new entries)
 */
const Insights = () => {
  const defaultPeriod = getHoldingPeriod();
  const [holdingMonth, setHoldingMonth] = useState(defaultPeriod.holdingMonth);
  const [holdingYear, setHoldingYear] = useState(defaultPeriod.holdingYear);
  const [category, setCategory] = useState("Large Cap");

  const variables = useMemo(
    () => ({ holdingMonth, holdingYear, category, limit: 25 }),
    [holdingMonth, holdingYear, category]
  );

  const { data: dataCA, loading: loadingCA, error: errorCA } = useQuery(
    COMMON_ADDITIONS,
    { variables }
  );
  const { data: dataCD, loading: loadingCD, error: errorCD } = useQuery(
    COMMON_DELETIONS,
    { variables }
  );
  const { data: dataCH, loading: loadingCH, error: errorCH } = useQuery(
    COMMON_HOLDINGS,
    { variables }
  );

  const hasError = errorCA || errorCD || errorCH;

  const renderSection = (title, baseColumns, nestedColumns, data, loading) => (
    <div className="insights-section">
      <Card className="insights-section-card" bordered={false} bodyStyle={{ padding: 0 }}>
        <div className="insights-section-header">
          <Title level={4} className="insights-section-title">
            {title}
          </Title>
        </div>
        <NestedHoldingsTable
          baseColumns={baseColumns}
          nestedColumns={nestedColumns}
          data={data || []}
          loading={loading}
        />
      </Card>
    </div>
  );

  // Common Additions - using % change
  const baseColumnsCA = [
    { title: "Stock Name", dataIndex: "stockName", key: "stockName" },
    { title: "Sector", dataIndex: "stockSector", key: "stockSector" },
    { title: "Funds", dataIndex: "fundCount", key: "fundCount", render: numberFormat },
    { 
      title: "Avg % Change", 
      dataIndex: "avgPercentChange", 
      key: "avgPercentChange", 
      render: (val) => val ? `+${val.toFixed(2)}%` : '-',
    },
    { 
      title: "Total Value (Mn)", 
      dataIndex: "totalValueInMn", 
      key: "totalValueInMn", 
      render: numberFormat 
    },
  ];
  const nestedColumnsCA = [
    { 
      title: "Fund", 
      dataIndex: "mutualFundName", 
      key: "mutualFundName",
      render: (text, record) => (
        <Link 
          to={`/fund-details/${record.mutualFundId}`}
          style={{ color: "#1890ff", textDecoration: "none" }}
        >
          {text}
        </Link>
      ),
    },
    { 
      title: "% Change", 
      dataIndex: "percentChange", 
      key: "percentChange", 
      render: (val) => val ? `+${val.toFixed(2)}%` : '-'
    },
    { title: "Value (Mn)", dataIndex: "valueInMn", key: "valueInMn", render: numberFormat },
  ];

  // Common Deletions - using % change
  const baseColumnsCD = [
    { title: "Stock Name", dataIndex: "stockName", key: "stockName" },
    { title: "Sector", dataIndex: "stockSector", key: "stockSector" },
    { title: "Funds", dataIndex: "fundCount", key: "fundCount", render: numberFormat },
    { 
      title: "Avg % Change", 
      dataIndex: "avgPercentChange", 
      key: "avgPercentChange", 
      render: (val) => val ? `${val.toFixed(2)}%` : '-',
    },
    { 
      title: "Total Value (Mn)", 
      dataIndex: "totalValueInMn", 
      key: "totalValueInMn", 
      render: numberFormat 
    },
  ];
  const nestedColumnsCD = [
    { 
      title: "Fund", 
      dataIndex: "mutualFundName", 
      key: "mutualFundName",
      render: (text, record) => (
        <Link 
          to={`/fund-details/${record.mutualFundId}`}
          style={{ color: "#1890ff", textDecoration: "none" }}
        >
          {text}
        </Link>
      ),
    },
    { 
      title: "% Change", 
      dataIndex: "percentChange", 
      key: "percentChange", 
      render: (val) => val ? `${val.toFixed(2)}%` : '-'
    },
    { title: "Value (Mn)", dataIndex: "valueInMn", key: "valueInMn", render: numberFormat },
  ];

  // Common Holdings - using value only (no quantity)
  const baseColumnsCH = [
    { title: "Stock Name", dataIndex: "stockName", key: "stockName" },
    { title: "Sector", dataIndex: "stockSector", key: "stockSector" },
    { title: "Funds", dataIndex: "fundCount", key: "fundCount", render: numberFormat },
    { title: "Total Value (Mn)", dataIndex: "totalValueInMn", key: "totalValueInMn", render: numberFormat },
  ];
  const nestedColumnsCH = [
    { 
      title: "Fund", 
      dataIndex: "mutualFundName", 
      key: "mutualFundName",
      render: (text, record) => (
        <Link 
          to={`/fund-details/${record.mutualFundId}`}
          style={{ color: "#1890ff", textDecoration: "none" }}
        >
          {text}
        </Link>
      ),
    },
    { title: "Value (Mn)", dataIndex: "valueInMn", key: "valueInMn", render: numberFormat },
    { 
      title: "Weightage", 
      dataIndex: "percentageOfTotalHoldings", 
      key: "percentageOfTotalHoldings", 
      render: (val) => val ? `${val.toFixed(2)}%` : '-'
    },
  ];

  return (
    <div className="insights-page">
      <div className="insights-header">
        <div className="insights-header-left">
          <Title level={3} className="insights-header-title">
            Fund Insights
          </Title>
          <span className="insights-header-subtitle">
            Compare holdings, additions, and deletions across fund categories.
          </span>
        </div>
      </div>

      <Tab onTabChange={(key) => setCategory(key)} activeKey={category} />

      <div className="insights-filter-group insights-filter-group--top">
        <PeriodSelector
          holdingMonth={holdingMonth}
          holdingYear={holdingYear}
          onMonthChange={setHoldingMonth}
          onYearChange={setHoldingYear}
        />
      </div>

      {hasError ? (
        <p>{errorCA?.message || errorCD?.message || errorCH?.message}</p>
      ) : (
        <>
          {renderSection(
            "Common Additions",
            baseColumnsCA,
            nestedColumnsCA,
            dataCA?.getCommonFundAdditions,
            loadingCA
          )}
          {renderSection(
            "Common Deletions",
            baseColumnsCD,
            nestedColumnsCD,
            dataCD?.getCommonFundDeletions,
            loadingCD
          )}
          {renderSection(
            "Common Holdings",
            baseColumnsCH,
            nestedColumnsCH,
            dataCH?.getCommonHoldings,
            loadingCH
          )}
        </>
      )}
    </div>
  );
};

export default Insights;
